import { AnyAction } from 'redux';
import {
 ExpertsApplyModel,
 Recipient,
} from '../domain/ExpertsApplyFormModel';
import { MODULE_NAME } from '../moduleName';
import {
 PagedResponse,
 PatchWithMeta,
 QueryParams,
} from '@ofqual/portal-core/core/types';
import { QualificationModel } from '../domain/QualificationModel';
import { ApprovalSubject, AssessmentEvaluation, Evaluation, IndustryEvaluation, TeachingEvaluation } from '../domain/ApprovalSubject';
import { ChangeApplicationStatusModel } from '../presentation/types';
import { MessageThread } from '@ofqual/portal-activity-feed/activityfeed/domain/PublicMessageModel';
import { SubjectModel } from '../domain/SubjectModel';

export interface ActionPayload<T> extends AnyAction {
 type: string;
 payload: T;
}
export const LOADED_ITEM = `${MODULE_NAME}/LOADED_ITEM`;
export const LOADED_ITEMS = `${MODULE_NAME}/LOADED_ITEMS`;
export const LOADING_ITEM = `${MODULE_NAME}/LOADING_ITEM`;
export const LOADING_ITEMS = `${MODULE_NAME}/LOADING_ITEMS`;
export const UPDATE_APPLICATION_STATUS = `${MODULE_NAME}/UPDATE_APPLICATION_STATUS`;
export const SEND_PUBLIC_MESSAGE = `${MODULE_NAME}/SEND_PUBLIC_MESSAGE`;
export const UPDATING_ITEM = `${MODULE_NAME}/UPDATING_ITEM`;
export const UPDATING_ITEM_VALIDATION_FAIL = `${MODULE_NAME}/UPDATING_ITEM_VALIDATION_FAIL`;
export const ADD_CASE_NOTE = `${MODULE_NAME}/ADD_CASE_NOTE`;
export const ADD_CASE_NOTE_ERROR = `${MODULE_NAME}/ADD_CASE_NOTE_ERROR`;
export const ADD_INTERNAL_MESSAGE = `${MODULE_NAME}/ADD_INTERNAL_MESSAGE`;
export const LOADED_MESSAGES = `${MODULE_NAME}/LOADED_MESSAGES`;
export const UPDATE_OFQUALDetails = `${MODULE_NAME}/UPDATE_OFQUAL_DETAILS`;

export const GET_UNREAD_MESSAGES_COUNT = `${MODULE_NAME}/GET_UNREAD_MESSAGES_COUNT`;
export const GOT_UNREAD_MESSAGES_COUNT = `${MODULE_NAME}/GOT_UNREAD_MESSAGES_COUNT`;
export const MARK_MESSAGES_AS_READ = `${MODULE_NAME}/MARK_MESSAGES_AS_READ`;

export const GET_MESSAGE_THREADS = `${MODULE_NAME}/GET_MESSAGE_THREADS`;
export const GOT_MESSAGE_THREADS = `${MODULE_NAME}/GOT_MESSAGE_THREADS`;

export const GET_ALL_EVALUATIONS = `${MODULE_NAME}/GET_ALL_EVALUATIONS`;
export const GOT_ALL_EVALUATIONS = `${MODULE_NAME}/GOT_ALL_EVALUATIONS`;

export const UPDATE_ASSESSMENT_APPROVAL = `${MODULE_NAME}/UPDATE_ASSESSMENT_APPROVAL`;
export const UPDATE_TEACHING_APPROVAL = `${MODULE_NAME}/UPDATE_TEACHING_APPROVAL`;
export const UPDATE_INDUSTRY_APPROVAL = `${MODULE_NAME}/UPDATE_INDUSTRY_APPROVAL`;

export const UPDATED_ASSESSMENT_EXPERIENCE = `${MODULE_NAME}/UPDATED_ASSESSMENT_EXPERIENCE`;
export const UPDATED_INDUSTRY_APPROVAL = `${MODULE_NAME}/UPDATED_INDUSTRY_APPROVAL`;
export const UPDATED_TEACHING_APPROVAL = `${MODULE_NAME}/UPDATED_TEACHING_APPROVAL`;

export const UPDATE_EXPERTISE = `${MODULE_NAME}/UPDATE_EXPERTISE`;

export const loadedItem = (item: ExpertsApplyModel) => ({
 type: LOADED_ITEM,
 payload: item,
});

export const loadingItem = (id: string) => ({
 type: LOADING_ITEM,
 payload: id,
});

export const loadedItems = (payload: PagedResponse<ExpertsApplyModel>) => ({
 type: LOADED_ITEMS,
 payload,
});

export const loadingItems = (payload: QueryParams) => ({
 type: LOADING_ITEMS,
 payload,
});

export const updateApplicationStatus = (
 id: string,
 applicationStatus: ChangeApplicationStatusModel,
) => ({
 type: UPDATE_APPLICATION_STATUS,
 payload: { id, applicationStatus },
});

export const sendPublicMessage = (
 id: string,
 files: File[],
 subject: string,
 message: string,
 threadId?: string,
) => ({
 type: SEND_PUBLIC_MESSAGE,
 payload: { id, files, subject, message, threadId },
});

export const getMessageThreads = (citizenId: string) => ({
 type: GET_MESSAGE_THREADS,
 payload: { citizenId },
});

export const gotMessageThreads = (messageThreads: MessageThread[]) => ({
    type: GOT_MESSAGE_THREADS,
    payload: {messageThreads},
});

export const markMessagesAsRead = (messageThread: MessageThread) => ({
    type: MARK_MESSAGES_AS_READ,
    payload: {messageThread},
})

export const getUnreadMessageCount = (citizenId: string) => ({
 type: GET_UNREAD_MESSAGES_COUNT,
 payload: { citizenId },
});

export const getAllEvaluations = (citizenId: string) => ({
 type: GET_ALL_EVALUATIONS,
 payload: { citizenId },
});

export const gotAllEvaluations = (evaluation: Evaluation) => ({
 type: GOT_ALL_EVALUATIONS,
 payload: { evaluation },
});

export const updatingItem = (
 id: string,
 qualifications: QualificationModel[],
 patchDocument: PatchWithMeta<ExpertsApplyModel>,
) => ({
 type: UPDATING_ITEM,

 payload: { id, qualifications, patchDocument },
});

export const addCaseNote = (
 id: string,
 caseFiles: File[],
 caseNote: string,
 subject: string,
) => ({
 type: ADD_CASE_NOTE,
 payload: { id, caseFiles, caseNote, subject },
});

export const addCaseNoteError = (message: string) => ({
 type: ADD_CASE_NOTE_ERROR,
 payload: { message },
});

export const addInternalMessage = (
 id: string,
 files: File[],
 subject: string,
 message: string,
 recipients: Recipient[],
) => ({
 type: ADD_INTERNAL_MESSAGE,
 payload: { id, files, subject, message, recipients },
});

export const gotUnreadMessageCount = (count: number) => ({
 type: GOT_UNREAD_MESSAGES_COUNT,
 payload: count,
});

export const updateAssessmentExperienceApproval = (
 id: string,
 approvalSubjects: ApprovalSubject[],
 assessmentEvaluation: AssessmentEvaluation | undefined,
 send: boolean
) => ({
 type: UPDATE_ASSESSMENT_APPROVAL,
 payload: { id, approvalSubjects,  assessmentEvaluation, send },
});

export const updatedAssessmentExperienceApproval = () => ({
 type: UPDATED_ASSESSMENT_EXPERIENCE,
});

export const updateTeachingExperienceApproval = (
 id: string,
 approvalSubjects: ApprovalSubject[],
 teachingEvaluation: TeachingEvaluation | undefined,
 send: boolean
) => ({
 type: UPDATE_TEACHING_APPROVAL,
 payload: { id, approvalSubjects, teachingEvaluation, send },
});

export const updateIndustryExperienceApproval = (
 id: string,
 approvalSubjects: ApprovalSubject[],
 industryEvaluation: IndustryEvaluation | undefined,
 send: boolean
) => ({
 type: UPDATE_INDUSTRY_APPROVAL,
 payload: { id, approvalSubjects, industryEvaluation, send },
});

export const addExpertise = (
 id: string,
 expertise : SubjectModel
) => ({
    type: UPDATE_EXPERTISE,
    payload: {id, expertise}
})